import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ThemeProvider } from 'react-jss';
import { ReactNotifications } from 'react-notifications-component';
import { useCookie, useEffectOnce, useFullscreen } from 'react-use';
import { Route, Routes, useLocation } from 'react-router-dom';
import { ToastProvider } from 'react-toast-notifications';
import jwtDecode from 'jwt-decode';
import { TourProvider } from '@reactour/tour';
import Swal from 'sweetalert2';
import ThemeContext from '../contexts/themeContext';

import Wrapper from '../layout/Wrapper/Wrapper';
import Portal from '../layout/Portal/Portal';
import { Toast, ToastContainer } from '../components/bootstrap/Toasts';
import Modal, { ModalBody } from '../components/bootstrap/Modal';
import useDarkMode from '../hooks/useDarkMode';
import COLORS from '../common/data/enumColors';
import { getOS, cookieNameGenerator } from '../helpers/helpers';
import steps, { styles } from '../steps';
import Aside from '../layout/Aside/Aside';
import AuthModule from '../modules/AuthModule';
import OidcModule from '../modules/OidcModule';

const accessToken = localStorage.getItem('accessToken') || null;
const appToken = localStorage.getItem('appToken') || null;
const IS_SSO = process.env.REACT_APP_SSO == 'true' || false;
const SSO_KEY = process.env.REACT_APP_SSO_KEY || null;
const SSO_HOMEPAGE = process.env.REACT_APP_SSO_HOMEPAGE || null;

const App = () => {
	const location = useLocation();

	const [cookie] = useCookie([cookieNameGenerator('ssocookies')]);
	const [cookieAcc] = useCookie([cookieNameGenerator('ssoaccount')]);
	const [cookiePhone] = useCookie([cookieNameGenerator('ssophonenumber')]);
	getOS();
	const [loadingSubmit, setLoadingSubmit] = useState(false);

	/**
	 * Dark Mode
	 */
	const { themeStatus, darkModeStatus } = useDarkMode();
	const theme = {
		theme: themeStatus,
		primary: COLORS.PRIMARY.code,
		secondary: COLORS.SECONDARY.code,
		success: COLORS.SUCCESS.code,
		info: COLORS.INFO.code,
		warning: COLORS.WARNING.code,
		danger: COLORS.DANGER.code,
		dark: COLORS.DARK.code,
		light: COLORS.LIGHT.code,
	};
	const testJSON = (text) => {
		if (typeof text !== 'string') {
			return false;
		}
		try {
			JSON.parse(text);
			return true;
		} catch (error) {
			return false;
		}
	};
	useEffectOnce(() => {
		const isSSOLogin = async () => {
			try {
				// Kegagalan login OIDC dipulangkan callback be-ellenkasquad lewat
				// `?oidc_error=`. Ditangani PALING AWAL -- sebelum blok appToken
				// di bawah, yang langsung `return` sambil memuat ulang halaman dan
				// akan membuang dialognya -- lalu alurnya DIHENTIKAN. Tanpa ini
				// cabang di bawah melihat "cookie SSO ada, accessToken belum" lalu
				// melempar pengguna ke OP lagi, dan OP memulangkannya dengan galat
				// yang sama: lingkaran tanpa akhir.
				//
				// `oidc_gagal` ikut dipasang: memuat ulang halaman (URL-nya sudah
				// bersih) jatuh ke jalur login lama selama tab ini terbuka.
				const galatOidc = OidcModule.aktif() ? OidcModule.ambilGalatDariUrl() : null;
				if (galatOidc) {
					OidcModule.bersihkanGalatDariUrl();
					sessionStorage.setItem('oidc_gagal', '1');
					// appToken tetap diminta (form login butuh), TANPA reload.
					if (!appToken) AuthModule.generateToken();
					Swal.fire({
						heightAuto: false,
						icon: 'error',
						title: 'Login Gagal',
						text: galatOidc,
					});
					return null;
				}

				if (!appToken) {
					return AuthModule.generateToken().then(() => {
						window.location.reload();
					});
				}

				// Penebusan tiket OIDC HARUS mendahului bootstrap SSO di bawah.
				// Kalau tidak, cabang "tidak ada kuki" di bawah melempar pengguna
				// ke beranda SSO, dan tiket ikut hangus -- gejalanya login yang
				// gagal senyap, bukan galat. Tanpa appToken blok di atas sudah
				// memuat ulang halaman; tiketnya masih di URL dan ditebus di sini
				// pada pemuatan berikutnya.
				const tiketOidc = OidcModule.aktif() ? OidcModule.ambilTiketDariUrl() : null;
				if (tiketOidc) {
					setLoadingSubmit(true);
					try {
						const sesi = await OidcModule.tebusTiket(tiketOidc);
						const dipakai = await OidcModule.terapkanSesi(sesi);
						OidcModule.bersihkanTiketDariUrl();
						if (dipakai) {
							sessionStorage.removeItem('oidc_gagal');
							return window.location.reload();
						}
						// Sesi tidak layak (tanpa accessToken / current_person / peran default).
						sessionStorage.setItem('oidc_gagal', '1');
					} catch (errTiket) {
						OidcModule.bersihkanTiketDariUrl();
						// Penanda ini mencegah lingkaran: tanpa itu jalur di bawah
						// akan mengalihkan ke OIDC lagi, gagal lagi, selamanya.
						sessionStorage.setItem('oidc_gagal', '1');
					} finally {
						setLoadingSubmit(false);
					}
				}

				const isEmailSSO = cookieAcc ? window.atob(cookieAcc) : null;

				const isHPSSO = cookiePhone ? window.atob(cookiePhone) : null;
				if (location.pathname.match('login-guest')) {
					return isEmailSSO;
				}
				if (location.pathname.match('privacy-policy')) {
					return isEmailSSO;
				}
				if (location.pathname.match('terms-and-conditions')) {
					return isEmailSSO;
				}
				if (IS_SSO) {
					if (!cookie) {
						AuthModule.goONELNKHomapage();
						window.location.href = SSO_HOMEPAGE;
						return SSO_HOMEPAGE;
					}
					const decryptCookieSSO = window.atob(cookie);
					if (!decryptCookieSSO) {
						AuthModule.goONELNKHomapage();
						window.location.href = SSO_HOMEPAGE;
						return SSO_HOMEPAGE;
					}
					const tokenAccSSO = testJSON(decryptCookieSSO)
						? JSON.parse(decryptCookieSSO)
						: null;
					if (!tokenAccSSO?.sso_key) {
						AuthModule.goONELNKHomapage();
						window.location.href = SSO_HOMEPAGE;
						return SSO_HOMEPAGE;
					}
					if (tokenAccSSO?.sso_key && tokenAccSSO?.sso_key !== SSO_KEY) {
						AuthModule.goONELNKHomapage();
						window.location.href = SSO_HOMEPAGE;
						return SSO_HOMEPAGE;
					}

					const decodeToken = tokenAccSSO ? jwtDecode(tokenAccSSO?.accessToken) : null;
					//  isEmailSSo then get accessToken
					if (isEmailSSO && decodeToken && !accessToken) {
						setLoadingSubmit(true);
						const parseEmail = JSON.parse(isEmailSSO);
						const newPayload = {
							name: parseEmail?.name,
							email: parseEmail?.idTokenClaims?.preferred_username,
							account_id: parseEmail?.localAccountId,
							username: parseEmail?.localAccountId,
						};
						return AuthModule.loginSSO(newPayload)
							.then(async (res) => {
								const verifyToken = jwtDecode(res.accessToken);
								localStorage.setItem('accessToken', JSON.stringify(res));
								localStorage.setItem('roles', JSON.stringify(res.role));
								const new_roles = res.role
									.map((x) => {
										if (x.is_default) {
											return x;
										}
										return null;
									})
									.filter((e) => e);
								localStorage.setItem(
									'active_role',
									JSON.stringify({
										default_role_name: new_roles[0].role_name,
										default_role_code: new_roles[0].role_code,
										default_segment_code: new_roles[0].segment_code,
										default_segment_name: new_roles[0].segment_name,
									}),
								);
								let role = 'guest user';
								let segment = '';
								if (verifyToken.details.hris_org_tree.current_person) {
									if (verifyToken.details._id) {
										// employee from user auth service
										role = new_roles[0].role_code;
										segment = new_roles[0].segment_code;
									} else {
										// user from Authentication
										role = new_roles[0].role_code;
										segment = new_roles[0].segment_code;
									}
									localStorage.setItem('roles', JSON.stringify(res.role));
									localStorage.setItem(
										'photo',
										JSON.stringify({ ...res?.photo }),
									);

									// get menu
									await AuthModule.getMenuByRoleAndSegment(role, segment).then(
										() => {
											window.location.reload();
										},
									);
								}
							})
							.finally(() => {
								setLoadingSubmit(false);
							});
					}
					// login by phone
					if (
						!isEmailSSO &&
						isHPSSO &&
						decodeToken &&
						!accessToken &&
						tokenAccSSO?.sso_key
					) {
						setLoadingSubmit(true);

						const parseEmail = JSON.parse(isHPSSO);
						return AuthModule.loginPhone(parseEmail)
							.then(async (res) => {
								const verifyToken = jwtDecode(res.accessToken);
								localStorage.setItem('accessToken', JSON.stringify(res));
								localStorage.setItem('roles', JSON.stringify(res.role));
								const new_roles = res.role
									.map((x) => {
										if (x.is_default) {
											return x;
										}
										return null;
									})
									.filter((e) => e);
								localStorage.setItem(
									'active_role',
									JSON.stringify({
										default_role_name: new_roles[0].role_name,
										default_role_code: new_roles[0].role_code,
										default_segment_code: new_roles[0].segment_code,
										default_segment_name: new_roles[0].segment_name,
									}),
								);
								let role = 'guest user';
								let segment = '';
								if (verifyToken.details.hris_org_tree.current_person) {
									if (verifyToken.details._id) {
										// employee from user auth service
										role = new_roles[0].role_code;
										segment = new_roles[0].segment_code;
									} else {
										// user from Authentication
										role = new_roles[0].role_code;
										segment = new_roles[0].segment_code;
									}
									localStorage.setItem('roles', JSON.stringify(res.role));

									// get menu
									await AuthModule.getMenuByRoleAndSegment(role, segment).then(
										() => {
											window.location.reload();
										},
									);
								}
							})
							.catch((err) => {
								Swal.fire({
									heightAuto: false,
									title: 'Warning!',
									text: err,
									icon: 'info',
								});
							})
							.finally(() => {
								setLoadingSubmit(false);
							});
					}
					//  !isEmailSSo && !isHPSSO then get accessToken
					if (
						!isEmailSSO &&
						!isHPSSO &&
						decodeToken &&
						!accessToken &&
						tokenAccSSO?.sso_key
					) {
						// Pengalihan OIDC MENGGANTIKAN langkah di bawah ini, yaitu
						// login ulang memakai password yang dibawa cookie SSO.
						// Letaknya sengaja BERSARANG di sini: di titik ini cookie SSO
						// yang sah sudah terbukti ada, jadi akun guest/mitra dan
						// pengunjung anonim tidak pernah menyentuhnya.
						if (
							OidcModule.aktif() &&
							OidcModule.bisaDiJendelaIni() &&
							!sessionStorage.getItem('oidc_gagal')
						) {
							window.location.href = OidcModule.urlLoginUntukHalamanIni();
							return null;
						}
						setLoadingSubmit(true);
						const accTokenParse = jwtDecode(tokenAccSSO.accessToken);
						return AuthModule.login(
							accTokenParse.username.toUpperCase(),
							accTokenParse.password,
						)
							.then(async (res) => {
								if (res.accessToken) {
									const verifyToken = jwtDecode(res.accessToken);
									localStorage.setItem('accessToken', JSON.stringify(res));
									localStorage.setItem('roles', JSON.stringify(res.role));
									const new_roles = res.role
										.map((x) => {
											if (x.is_default) {
												return x;
											}
											return null;
										})
										.filter((e) => e);
									localStorage.setItem(
										'active_role',
										JSON.stringify({
											default_role_name: new_roles[0].role_name,
											default_role_code: new_roles[0].role_code,
											default_segment_code: new_roles[0].segment_code,
											default_segment_name: new_roles[0].segment_name,
										}),
									);
									let role = 'guest user';
									let segment = '';
									if (verifyToken.details.hris_org_tree.current_person) {
										if (verifyToken.details._id) {
											// employee from user auth service
											role = new_roles[0].role_code;
											segment = new_roles[0].segment_code;
										} else {
											// user from Authentication
											role = new_roles[0].role_code;
											segment = new_roles[0].segment_code;
										}
										// get menu
										await AuthModule.getMenuByRoleAndSegment(
											role,
											segment,
										).then(() => {
											localStorage.setItem(
												'photo',
												JSON.stringify({ ...res?.photo }),
											);
											window.location.reload();
										});
									}
								}
							})
							.finally(() => {
								setLoadingSubmit(false);
							});
					}
				}
				return isEmailSSO;
			} catch (err) {
				return Swal.fire({
					heightAuto: false,
					title: 'Warning!',
					text: err,
					icon: 'info',
				});
			}
		};
		isSSOLogin();
	});

	useEffect(() => {
		if (darkModeStatus) {
			document.documentElement.setAttribute('theme', 'dark');
		}
		return () => {
			document.documentElement.removeAttribute('theme');
		};
	}, [darkModeStatus]);

	/**
	 * Full Screen
	 */
	const { fullScreenStatus, setFullScreenStatus } = useContext(ThemeContext);
	const ref = useRef(null);
	useFullscreen(ref, fullScreenStatus, {
		onClose: () => setFullScreenStatus(false),
	});

	/**
	 * Modern Design
	 */
	useLayoutEffect(() => {
		if (process.env.REACT_APP_MODERN_DESGIN === 'true') {
			document.body.classList.add('modern-design');
		} else {
			document.body.classList.remove('modern-design');
		}
	});

	//	Add paths to the array that you don't want to be "Aside".
	const withOutAsidePages = ['login'];

	return (
		<ThemeProvider theme={theme}>
			<ToastProvider components={{ ToastContainer, Toast }}>
				<TourProvider
					steps={steps}
					styles={styles}
					showNavigation={false}
					showBadge={false}>
					<Modal
						isOpen={loadingSubmit}
						size='sm'
						isCentered
						setIsOpen={() => {}}
						isStaticBackdrop>
						<ModalBody
							style={{ backgroundColor: '#6c5dd3', color: 'white' }}
							className='text-center'>
							<button className='btn btn-primary' type='button' disabled>
								<span
									className='spinner-grow spinner-grow-sm'
									role='status'
									aria-hidden='true'
								/>
								&nbsp;
								<span
									className='spinner-grow spinner-grow-sm'
									role='status'
									aria-hidden='true'
								/>
								&nbsp;
								<span
									className='spinner-grow spinner-grow-sm'
									role='status'
									aria-hidden='true'
								/>
								&nbsp;
								<span className='sr-only'>Loading...</span>
							</button>
						</ModalBody>
					</Modal>
					<div
						ref={ref}
						className='app'
						style={{
							backgroundColor: fullScreenStatus && 'var(--bs-body-bg)',
							zIndex: fullScreenStatus && 1,
							overflow: fullScreenStatus && 'scroll',
						}}>
						<Routes>
							{withOutAsidePages.map((path) => (
								<Route key={path} path={path} />
							))}
							<Route path='*' element={accessToken ? <Aside /> : null} />
						</Routes>
						<Wrapper />
					</div>
					<Portal id='portal-notification'>
						<ReactNotifications />
					</Portal>
				</TourProvider>
			</ToastProvider>
		</ThemeProvider>
	);
};

export default App;
